import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCreditCard, faEarthAmericas, faTimes, faTrophy, faXmark, faUsers } from "@fortawesome/pro-regular-svg-icons";
import { StatusCode } from "api/protocols";

import classNames from "classnames";
import { ButtonNew as Button } from "components/buttonNew";
import Callout from "components/callout/Callout";
import Card from "components/card/Card";
import Checkbox from "components/form/checkbox/Checkbox";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Page from "components/page/Page";
import Drawer from "components/drawerSlide/drawer";
import AddFolderProductsModal from "components/folders/AddFolderProductsModal";
import { useAddFolderProductsPicker } from "hooks/folders/useAddFolderProductsPicker";
import { capitalize, formatDate, parseLocalDateString } from "helpers/Helpers";
import { LocaleCurrency } from "helpers/Locale";
import moment from "moment";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory, useParams } from "react-router-dom";
import { IUIActions, showError } from "redux/actions/ui";
import { IProduct, IVariant } from "redux/reducers/models/product";
import { IBlock, ILocation, IModule, ISegment } from "redux/reducers/models/reservations";
import { Select } from "components/select/index";
import SelectNew from "components/select/SelectNew";
import "./editSegments.scss";
import { useSegmentsBesideDrawer } from "./useSegmentsBesideDrawer";
import YesNoSetting from "./YesNoSetting";
import { applySelectionSubtitle } from "../settings/reservations/unitLabel";
import { isEqualWith } from "lodash";
import { Badge } from "components/badge/Badge";
import { useTranslation } from "react-i18next";
import Popup from "components/popup/Popup";
import { ITournament } from "redux/reducers/models/tournament";
import { ILeague } from "redux/reducers/models/league";
import Icon from "components/icon/Icon";
import { useAppDispatch } from "hooks/redux";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import {
  DeleteReservationSegment,
  GetReservationLocation,
  GetReservationModule,
  GetReservationSegment,
  PutReservationSegment,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";

interface IAttachedVariant {
  variant: IVariant;
  product: IProduct;
  bookingMinutes: number | null;
}

interface IProps {
  uiActions: IUIActions;
}
interface IState {
  module: IModule;
  locations: Array<ILocation>;
  selectedDate: Date;
  productsModalVisible: boolean;
  segments: { [key: string]: Array<ISegment> };
  leagues: Array<Record<string, any>>;
  tournaments: Array<Record<string, any>>;
  deleteSegmentsPopupVisible: boolean;
}

interface IParamState {
  blocked_type: string;
  league_id: number;
  tournament_id: number;
  //pre_paid_required: boolean;
  credit_card_required: boolean | null;
  web_booking_enabled: boolean | null;
  attachedVariants: Array<IAttachedVariant>;
  productsEdited: boolean;
}

const PRODUCT_SELECT_LIMIT = 5;

interface IFilterState {
  tournamentSearchValue: string;
  leagueSearchValue: string;
  tournaments: Array<ITournament>;
  tournamentSearching: boolean;
  selectedTournament: ITournament;
  leagues: Array<ILeague>;
  leagueSearching: boolean;
  selectedLeague: ILeague;
}

export default function EditSegments(props: IProps) {
  const { uiActions } = props;
  const dispatch = useAppDispatch();

  const [state, setState] = useState<IState>({
    module: null,
    locations: [],
    selectedDate: parseLocalDateString(new URLSearchParams(window.location.search).get("date")) ?? new Date(),
    segments: null,
    productsModalVisible: false,
    leagues: [],
    tournaments: [],
    deleteSegmentsPopupVisible: false,
  });

  const [editSegments, setEditSegments] = useState<Array<number>>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const segmentsContainerRef = useSegmentsBesideDrawer(settingsOpen);
  const [settingsFormKey, setSettingsFormKey] = useState(0);

  const [segments, setSegments] = useState<Array<ISegment>>(undefined);
  const [segmentsBeforeChanges, setSegmentsBeforeChanges] = useState<Array<ISegment>>(undefined);
  const [tournaments, setTournaments] = useState<Array<ITournament>>([]);
  const [leagues, setLeagues] = useState<Array<ILeague>>([]);

  const [paramState, setParamState] = useState<IParamState>({
    blocked_type: "none",
    league_id: -2,
    tournament_id: -2,
    credit_card_required: null,
    web_booking_enabled: null,
    attachedVariants: [],
    productsEdited: false,
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    tournamentSearchValue: "",
    leagueSearchValue: "",
    tournaments: [],
    tournamentSearching: false,
    selectedTournament: null,
    leagues: [],
    leagueSearching: false,
    selectedLeague: null,
  });

  const [paramStateBeforeChanges, setParamStateBeforeChanges] = useState<IParamState>(undefined);

  const [moduleLoaded, setModuleLoaded] = useState<boolean>(false);
  const [segmentsLoaded, setSegmentsLoaded] = useState<boolean>(false);

  const { moduleId } = useParams<{ moduleId: string }>();
  const history = useHistory();
  const cameFromCalendar = new URLSearchParams(history.location.search).get("from") === "calendar";
  const { t } = useTranslation();
  const { Option } = Select;

  const productPicker = useAddFolderProductsPicker({
    open: state.productsModalVisible,
    maxSelections: PRODUCT_SELECT_LIMIT,
    includeVariants: true,
    countVariants: true,
    onError: message => uiActions.showError(t(message)),
  });

  const blockedTypes = ["none", "open", "blocked"];

  //The id of the last selected segment. Used to determine starting point of shift-click multi-select
  const [selectedSegmentId, setSelectedSegmentId] = useState<number>(null);

  useEffect(() => {
    void getModule();
  }, []);

  useEffect(() => {
    if (state.selectedDate) {
      void getSegments();
    }
  }, [state.selectedDate]);

  async function getModule() {
    uiActions.enqueue();
    const moduleRes = await GetReservationModule({ id: Number(moduleId) }, false);
    if (moduleRes?.status !== StatusCode.OK) {
      history.push("/admin/settings/reservations/modules");
      uiActions.showError(t("secure.facility.reservation.edit_segments.002"));
      uiActions.dequeue();
      return;
    }
    const locationRes = await GetReservationLocation({ reservation_module_id: Number(moduleId) }, false);
    if (locationRes.status !== StatusCode.OK) {
      history.push("/admin/settings/reservations/modules");
      uiActions.showError(t("secure.facility.reservation.edit_segments.003"));
      uiActions.dequeue();
      return;
    }

    const leaguesRes = await GetLeague(null, false);
    if (leaguesRes.status !== StatusCode.OK) {
      history.push("/admin/settings/reservations/modules");
      uiActions.showError(t("secure.facility.reservation.edit_segments.004"));
      uiActions.dequeue();
    }

    const tournamentRes = await GetTournament(null, false);
    if (tournamentRes.status !== StatusCode.OK) {
      history.push("/admin/settings/reservations/modules");
      uiActions.showError(t("secure.facility.reservation.edit_segments.005"));
      uiActions.dequeue();
    }
    uiActions.dequeue();

    ReactDOM.unstable_batchedUpdates(() => {
      setState(prevState => ({
        ...prevState,
        module: moduleRes.data,
        locations: locationRes?.data,
        //id of -1 sets the selected value to null. id of -2 means no selection has been made, value won't be overridden
        leagues: [{ name: "", id: -2 }, { name: "None", id: -1 }, ...leaguesRes.data],
        tournaments: [{ name: "", id: -2 }, { name: "None", id: -1 }, ...tournamentRes.data],
      }));
      setLeagues(leaguesRes.data);
      setTournaments(tournamentRes.data);
      setModuleLoaded(true);
    });
  }

  async function getSegments() {
    const segmentRes = await GetReservationSegment(
      { module_id: Number(moduleId), date: formatDate(state.selectedDate) },
      true,
    );
    if (segmentRes.status !== StatusCode.OK) {
      history.push(cameFromCalendar ? "/admin/settings/reservations/calendar" : "/admin/reservation");
      uiActions.showError(t("secure.facility.reservation.edit_segments.006"));
      return;
    }

    const formattedSegments = segmentRes.data?.reduce(
      (formattedSegments: { [key: string]: Array<ISegment> }, currentSegment: ISegment) => {
        return {
          ...formattedSegments,
          [currentSegment.start_time]: formattedSegments[currentSegment.start_time]
            ? [...formattedSegments[currentSegment.start_time], currentSegment]
            : [currentSegment],
        };
      },
      {},
    );
    ReactDOM.unstable_batchedUpdates(() => {
      setState(prevState => ({
        ...prevState,
        segments: formattedSegments,
      }));
      //Sort segments by start_time first then location_id
      setSegments(
        segmentRes?.data?.sort(
          (a: ISegment, b: ISegment) => a?.start_time?.localeCompare(b?.start_time) || a?.location_id - b?.location_id,
        ),
      );
      setEditSegments([]);
      setSegmentsBeforeChanges(segmentRes?.data);
      setSegmentsLoaded(true);
    });
  }

  function handleDateChange(date: Date) {
    setState(prevState => ({ ...prevState, selectedDate: date }));
  }

  function handleSegmentClick(segment_id: number, e: React.MouseEvent) {
    const shiftKeyPressed = e.shiftKey;

    const updatedSelectedSegmentIds = [...editSegments];
    const segmentIndex = updatedSelectedSegmentIds.indexOf(segment_id);
    if (segmentIndex === -1) {
      updatedSelectedSegmentIds.push(segment_id);
      setSettingsOpen(true);

      //Shift key not pressed - set state of last selected segment
      if (!shiftKeyPressed) {
        setSelectedSegmentId(segment_id);
      } else {
        //Shift key pressed - set range of segments
        void setSegmentSelectRange(segment_id);
        return;
      }
    } else {
      updatedSelectedSegmentIds.splice(segmentIndex, 1);
      if (updatedSelectedSegmentIds?.length > 0) {
        setSelectedSegmentId(updatedSelectedSegmentIds[updatedSelectedSegmentIds?.length - 1]);
      } else {
        setSelectedSegmentId(null);
      }
    }
    setEditSegments(updatedSelectedSegmentIds);
  }

  function setSegmentSelectRange(currentSegmentId: number) {
    const segmentsArray = [...segments];
    const currentSegmentIndex = segmentsArray?.findIndex(segment => segment?.id === currentSegmentId);
    const prevSegmentIndex = segmentsArray?.findIndex(segment => segment?.id === selectedSegmentId);

    if (currentSegmentIndex !== -1 && prevSegmentIndex !== -1) {
      //Only select segments in the same location
      const locationId = segmentsArray[prevSegmentIndex]?.location_id;
      let segmentRange: Array<number>;
      if (currentSegmentIndex > prevSegmentIndex) {
        segmentRange = segmentsArray
          ?.filter(filteredSegment => filteredSegment.booking_id === null)
          .slice(prevSegmentIndex, currentSegmentIndex + 1)
          ?.filter(filteredSegment => filteredSegment?.location_id === locationId)
          ?.map(segment => segment?.id);
      } else {
        segmentRange = segmentsArray
          ?.filter(filteredSegment => filteredSegment.booking_id === null)
          .slice(currentSegmentIndex, prevSegmentIndex + 1)
          ?.reverse()
          ?.filter(filteredSegment => filteredSegment?.location_id === locationId)
          ?.map(segment => segment?.id);
      }
      setSettingsOpen(true);
      setEditSegments(prevState => [...prevState, ...segmentRange]);
    } else {
      setSelectedSegmentId(null);
      void deselectAllSegments();
    }
  }

  function productsFromAttachedVariants(attached: Array<IAttachedVariant>): Array<IProduct> {
    const products: Array<IProduct> = [];
    attached.forEach(({ variant, product }) => {
      const existing = products.find(item => item.id === product.id);
      if (existing) {
        existing.variants = [...(existing.variants ?? []), variant];
        return;
      }
      products.push({ ...product, variants: [variant] });
    });
    return products;
  }

  function attachedVariantsFromProducts(
    products: Array<IProduct>,
    previous: Array<IAttachedVariant> = [],
  ): Array<IAttachedVariant> {
    const minutesByVariantId = new Map(previous.map(item => [item.variant.id, item.bookingMinutes ?? null]));
    const attached: Array<IAttachedVariant> = [];
    products.forEach(product => {
      (product.variants ?? []).forEach(variant => {
        attached.push({
          variant,
          product,
          bookingMinutes: minutesByVariantId.get(variant.id) ?? null,
        });
      });
    });
    return attached;
  }

  function closeProductModal() {
    setState(prevState => ({
      ...prevState,
      productsModalVisible: false,
    }));
    productPicker.resetPicker();
  }

  function openProductModal() {
    productPicker.openPicker({
      products: productsFromAttachedVariants(paramState.attachedVariants),
      quantities: {},
    });
    setState(prevState => ({ ...prevState, productsModalVisible: true }));
  }

  function attachProducts(products: Array<IProduct>) {
    setParamState(prevState => ({
      ...prevState,
      attachedVariants: attachedVariantsFromProducts(products, prevState.attachedVariants),
      productsEdited: true,
    }));
    setState(prevState => ({ ...prevState, productsModalVisible: false }));
    productPicker.resetPicker();
  }

  function detachVariant(variantId: number) {
    setParamState(prevState => ({
      ...prevState,
      attachedVariants: prevState.attachedVariants.filter(item => item.variant.id !== variantId),
      productsEdited: true,
    }));
  }

  function setBookingMinutes(variantId: number, bookingMinutes: number | null) {
    setParamState(prevState => ({
      ...prevState,
      productsEdited: true,
      attachedVariants: prevState.attachedVariants.map(item =>
        item.variant.id === variantId ? { ...item, bookingMinutes } : item,
      ),
    }));
  }

  function selectAllSegments() {
    const allSegmentIds = segments?.map(segment => segment?.id);
    setSettingsOpen(true);
    setEditSegments(allSegmentIds);
  }

  function segmentIdsForLocation(locationId: number) {
    return (segments ?? []).filter(segment => segment?.location_id === locationId && segment?.id != null).map(segment => segment.id);
  }

  function toggleLocationSegments(locationId: number) {
    const ids = segmentIdsForLocation(locationId);
    if (ids.length === 0) {
      return;
    }
    const allSelected = ids.every(id => editSegments.includes(id));
    if (allSelected) {
      setEditSegments(prev => prev.filter(id => !ids.includes(id)));
      return;
    }
    setSettingsOpen(true);
    setEditSegments(prev => [...prev, ...ids.filter(id => !prev.includes(id))]);
  }

  function clearSelection() {
    deselectAllSegments();
    setSettingsOpen(false);
  }

  function clearSettings() {
    setParamState(prevState => ({
      ...(paramStateBeforeChanges ?? prevState),
      credit_card_required: null,
      web_booking_enabled: null,
    }));
    setSettingsFormKey(key => key + 1);
    void removeSelectedTournament();
    void removeSelectedLeague();
  }

  function settingsSubtitle() {
    if (!editSegments.length || !segments?.length) {
      return undefined;
    }

    const selected = segments.filter(segment => editSegments.includes(segment.id));
    const groups = (state.locations ?? [])
      .map(location => ({
        bay: location.title,
        times: selected
          .filter(segment => segment.location_id === location.id)
          .map(segment => moment(segment.start_time, "HH:mm:ss"))
          .filter(time => time.isValid())
          .sort((a, b) => a.valueOf() - b.valueOf())
          .map(time => time.format("h:mm A")),
      }))
      .filter(group => group.times.length > 0);

    return applySelectionSubtitle(groups, t);
  }

  function deselectAllSegments() {
    ReactDOM.unstable_batchedUpdates(() => {
      setEditSegments([]);
      setSelectedSegmentId(null);
    });
  }

  function handleDropDownChange(value: string, id: string) {
    setParamState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleApplyButton() {
    const updatedSegments = [...segments];
    editSegments?.forEach(segmentId => {
      const currentSegmentIndex = segments?.findIndex(segment => segment?.id === segmentId);
      const currentSegment = segments[currentSegmentIndex];

      if (currentSegmentIndex !== -1) {
        const attachedVariants = paramState.attachedVariants;
        const updatedBlocks = !paramState.productsEdited
          ? currentSegment?.blocks
          : attachedVariants.length > 0
          ? attachedVariants.map(attached => ({
              ...currentSegment.blocks?.[0],
              id: undefined,
              reservation_segment_id: currentSegment.id,
              product_title: attached.product?.preferred_title || attached.product?.title,
              variant_title: attached.variant?.title,
              product_variant_id: attached.variant?.id,
              variant_price: attached.variant?.price,
              booking_minutes: attached.bookingMinutes ?? null,
            }))
          : [
              {
                ...currentSegment.blocks?.[0],
                reservation_segment_id: currentSegment.id,
                product_title: null,
                variant_title: null,
                product_variant_id: null,
                variant_price: null,
                booking_minutes: null,
              },
            ];

        const updatedCurrentSegment: ISegment = {
          ...currentSegment,
          blocked_type: paramState?.blocked_type !== "none" ? paramState?.blocked_type : currentSegment?.blocked_type,
          league_id:
            paramState?.league_id >= 1
              ? paramState?.league_id
              : paramState?.league_id === -1
              ? null
              : currentSegment?.league_id,
          tournament_id:
            paramState?.tournament_id >= 1
              ? paramState?.tournament_id
              : paramState?.tournament_id === -1
              ? null
              : currentSegment?.tournament_id,
          credit_card_required:
            paramState.credit_card_required != null
              ? paramState.credit_card_required
              : currentSegment?.credit_card_required,
          web_booking_enabled:
            paramState.web_booking_enabled != null
              ? paramState.web_booking_enabled
              : currentSegment?.web_booking_enabled,
          blocks: updatedBlocks as Array<IBlock>,
          ...(paramState.productsEdited
            ? {
                variants: attachedVariants.map(attached => ({
                  product_variant_id: attached.variant.id,
                  booking_minutes: attached.bookingMinutes ?? null,
                })),
                variant_ids: attachedVariants.map(attached => attached.variant.id),
                ...(attachedVariants[0] ? { variant_id: attachedVariants[0].variant.id } : {}),
              }
            : {}),
        };

        updatedSegments[currentSegmentIndex] = updatedCurrentSegment;
      }
    });

    const formattedSegments = updatedSegments?.reduce(
      (formattedSegments: { [key: string]: Array<ISegment> }, currentSegment: ISegment) => {
        return {
          ...formattedSegments,
          [currentSegment.start_time]: formattedSegments[currentSegment.start_time]
            ? [...formattedSegments[currentSegment.start_time], currentSegment]
            : [currentSegment],
        };
      },
      {},
    );

    ReactDOM.unstable_batchedUpdates(() => {
      setParamState(paramStateBeforeChanges);
      setEditSegments([]);
      setSegments(prevState => (updatedSegments?.length > 0 ? updatedSegments : prevState));
      setState(prevState => ({ ...prevState, segments: formattedSegments }));
      setSettingsOpen(false);
    });
    void removeSelectedTournament();
    void removeSelectedLeague();
  }

  async function deleteSegments() {
    const deleteRes = await DeleteReservationSegment({ segment_ids: editSegments }, true);

    if (deleteRes.status !== StatusCode.OK) {
      uiActions.showError(t("Error deleting segment"));
      return;
    }
    setEditSegments([]);
    setState(prevState => ({ ...prevState, deleteSegmentsPopupVisible: false }));
    void getSegments();
  }

  function unsavedChangesExist() {
    if (segmentsBeforeChanges === undefined || paramStateBeforeChanges === undefined) {
      if (moduleLoaded && segmentsLoaded) {
        setSegmentsBeforeChanges(segments);
        setParamStateBeforeChanges(paramState);
      }
      return false;
    }

    return !isEqualWith(segments, segmentsBeforeChanges);
  }

  function cancelUnsavedChanges() {
    const formattedSegments = segmentsBeforeChanges?.reduce(
      (formattedSegments: { [key: string]: Array<ISegment> }, currentSegment: ISegment) => {
        return {
          ...formattedSegments,
          [currentSegment.start_time]: formattedSegments[currentSegment.start_time]
            ? [...formattedSegments[currentSegment.start_time], currentSegment]
            : [currentSegment],
        };
      },
      {},
    );
    ReactDOM.unstable_batchedUpdates(() => {
      setSegments(segmentsBeforeChanges);
      setState(prevState => ({ ...prevState, segments: formattedSegments }));
    });
  }

  async function saveSegments() {
    const changedSegments = segments?.filter((segment, index) => !isEqualWith(segment, segmentsBeforeChanges[index]));
    if (changedSegments?.length > 0) {
      const segmentRes = await PutReservationSegment({ segments: changedSegments }, true);
      if (segmentRes?.status !== StatusCode.OK) {
        uiActions.showError(t("secure.facility.reservation.edit_segments.007"));
        return;
      }
      uiActions.showSuccess(t("secure.facility.reservation.edit_segments.008"));
      void getSegments();
    }
  }

  function renderSegmentProducts(segment: ISegment) {
    const products = (segment?.blocks ?? []).filter(block => block?.product_variant_id);
    if (products.length === 0) {
      return null;
    }
    return (
      <div className="segment-products">
        {products.map(block => (
          <div key={block.product_variant_id} className="variant-group">
            <p className="variant-title">{block.product_title}</p>
            {block.product_title !== block.variant_title && <p className="variant-title">{block.variant_title}</p>}
            <LocaleCurrency style={{ fontSize: "0.7rem" }} amount={block.variant_price} />
            {block.booking_minutes === 30 || block.booking_minutes === 60 ? (
              <p className="variant-title">{t("{{count}} minutes", { count: block.booking_minutes })}</p>
            ) : null}
          </div>
        ))}
      </div>
    );
  }

  function getToSegmentsConfig() {
    history.push(`/admin/settings/reservations/module/${moduleId}`);
  }

  function removeSelectedTournament() {
    setFilterState(prevState => ({
      ...prevState,
      selectedTournament: null,
      tournaments: [],
      tournamentSearching: false,
      tournamentSearchValue: "",
    }));
    setParamState(prevState => ({ ...prevState, tournament_id: -2 }));
  }

  function removeSelectedLeague() {
    setFilterState(prevState => ({
      ...prevState,
      selectedLeague: null,
      leagues: [],
      leagueSearching: false,
      leagueSearchValue: "",
    }));
    setParamState(prevState => ({ ...prevState, league_id: -2 }));
  }

  function displayLeagues() {
    if (filterState?.leagues && filterState?.leagues?.length > 0 && filterState?.leagueSearchValue !== "") {
      return filterState?.leagues;
    } else {
      return leagues;
    }
  }

  function handleTournamentChange(value: any, id: string, tournament: ITournament) {
    setParamState(prevState => ({ ...prevState, [id]: tournament.id }));
    setFilterState(prevState => ({ ...prevState, tournamentSearchValue: "", selectedTournament: tournament }));
  }

  function handleLeagueChange(value: any, id: string, league: ILeague) {
    setParamState(prevState => ({ ...prevState, [id]: league.id }));
    setFilterState(prevState => ({ ...prevState, leagueSearchValue: "", selectedLeague: league }));
  }

  function displayTournaments() {
    if (filterState?.tournaments && filterState?.tournaments?.length > 0 && filterState?.tournamentSearchValue !== "") {
      return filterState?.tournaments;
    } else {
      return tournaments;
    }
  }

  async function loadSearchedTournaments(search: string) {
    const params = {
      search: search,
    };

    const tournamentRes = await GetTournament(params, false);

    if (tournamentRes.status !== StatusCode.OK) {
      dispatch(showError(tournamentRes.message));
      return [];
    }

    return tournamentRes.data;
  }

  async function loadSearchedLeagues(search: string) {
    const params = {
      search: search,
    };

    const leagueRes = await GetLeague(params, false);

    if (leagueRes.status !== StatusCode.OK) {
      dispatch(showError(leagueRes.message));

      return [];
    }

    return leagueRes.data;
  }

  // Search Tournaments
  useEffect(() => {
    let mounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const search = () => {
      timeoutId = global.setTimeout(() => {
        void (async () => {
          if (filterState.tournamentSearchValue !== "") {
            try {
              setFilterState(prevState => ({ ...prevState, tournamentSearching: true }));
              const tournaments = await loadSearchedTournaments(filterState.tournamentSearchValue);
              if (mounted) {
                setFilterState(prevState => ({ ...prevState, tournaments: tournaments, tournamentSearching: false }));
              }
            } catch (error) {
              console.log("err", error);
            }
            return;
          } else {
            setFilterState(prevState => ({ ...prevState, tournaments: [], tournamentSearching: false }));
          }
        })();
      }, 1000);
    };
    search();
    return () => {
      mounted = false;
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [filterState.tournamentSearchValue]);

  //Load Leagues
  useEffect(() => {
    let mounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const search = () => {
      timeoutId = global.setTimeout(() => {
        void (async () => {
          if (filterState.leagueSearchValue !== "") {
            try {
              setFilterState(prevState => ({ ...prevState, leagueSearching: true }));
              const leagues = await loadSearchedLeagues(filterState.leagueSearchValue);
              if (mounted) {
                setFilterState(prevState => ({ ...prevState, leagues: leagues, leagueSearching: false }));
              }
            } catch (error) {
              console.log("err", error);
            }
            return;
          } else {
            setFilterState(prevState => ({ ...prevState, leagues: [], leagueSearching: false }));
          }
        })();
      }, 1000);
    };
    search();
    return () => {
      mounted = false;
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [filterState.leagueSearchValue]);

  return (
    <>
      <Page
        title={state?.module?.title ?? ""}
        breadcrumbs={[
          cameFromCalendar
            ? { prefix: true, label: t("Template Calendar"), url: "/admin/settings/reservations/calendar" }
            : { prefix: true, label: t("Activities"), url: "/admin/reservation" },
        ]}
        notificationBarProps={{
          isVisible: unsavedChangesExist(),
          onAction: saveSegments,
          onCancel: cancelUnsavedChanges,
        }}
      >
        <Card>
          <Card.Section>
            <div className="edit-segments-options">
            <div className="edit-segments-button-group edit-segments-options-toolbar">
              <div className="edit-segments-date-container">
                <label className="edit-segment-label">{t("secure.facility.reservation.edit_segments.012")}</label>
                <DatePickerInput
                  months={1}
                  position="left"
                  startingDate={state.selectedDate}
                  setStartingDate={handleDateChange}
                />
              </div>
            </div>
            {editSegments.length > 0 ? (
              <div className="flex items-center gap-2 edit-segments-selection-bar">
                <p className="font-medium" style={{ padding: "0 18px 0 0" }}>
                  {editSegments.length} {t("selected")}
                </p>
                <div className="edit-segments-selection-divider" />
                <Button size="medium" type="text" onClick={selectAllSegments}>
                  {t("secure.facility.reservation.edit_segments.014")}
                </Button>
                <div className="edit-segments-selection-divider" />
                <Button size="medium" type="text" onClick={clearSelection}>
                  <FontAwesomeIcon icon={faXmark} className="mr-2" /> {t("Clear")}
                </Button>
                <div className="edit-segments-selection-divider" />
                <Button
                  size="medium"
                  type="text"
                  onClick={() => setState(prevState => ({ ...prevState, deleteSegmentsPopupVisible: true }))}
                  disabled={editSegments.length < 1}
                >
                  {t("secure.facility.reservation.edit_segments.042")}
                </Button>
                {!settingsOpen ? (
                  <>
                    <div className="edit-segments-selection-divider" />
                    <Button size="medium" type="text" onClick={() => setSettingsOpen(true)}>
                      {t("Open settings")}
                    </Button>
                  </>
                ) : null}
              </div>
            ) : !settingsOpen ? (
              <div className="edit-segments-selection-bar">
                <Button size="medium" type="text" onClick={() => setSettingsOpen(true)}>
                  {t("Open settings")}
                </Button>
              </div>
            ) : null}
            </div>

            {state.segments && Object.keys(state.segments)?.length > 0 ? (
              <div className="edit-segments">
                <div className="segments-container" ref={segmentsContainerRef}>
                  <table className="ui-table ui-table-condensed ui-table-separated-horizontal segments-table">
                    <thead>
                      <tr className="segments-table-header">
                        <td className="segments-table-times-header">
                          {t("secure.facility.reservation.edit_segments.027")}
                        </td>
                        {state?.locations?.map(location => {
                          const locationIds = segmentIdsForLocation(location.id);
                          const selectedCount = locationIds.filter(id => editSegments.includes(id)).length;
                          return (
                            <td key={location.id}>
                              <div className="segments-table-location">
                                <Checkbox
                                  id={`location-${location.id}`}
                                  size="small"
                                  checked={locationIds.length > 0 && selectedCount === locationIds.length}
                                  indeterminate={selectedCount > 0 && selectedCount < locationIds.length}
                                  disabled={locationIds.length === 0}
                                  onChange={() => toggleLocationSegments(location.id)}
                                />
                                <span>{location.title}</span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(state?.segments)?.map((segmentKey, segmentKeyIndex) => {
                        return (
                          <tr key={segmentKeyIndex}>
                            <td className="time-text">{moment(segmentKey, "hh:mm:ss").format("h:mm A")}</td>
                            {state?.locations?.map((location, locationIndex) => {
                              const segment = state?.segments[segmentKey]?.find(
                                searchSegment => searchSegment?.location_id === location?.id,
                              );
                              if (
                                (segment?.blocked_type === "open" || segment?.blocked_type === "open_reserved") &&
                                segment?.booking_id === null
                              ) {
                                return (
                                  <td
                                    key={locationIndex}
                                    className={classNames("segment-available", {
                                      "segment-selected": editSegments.includes(segment?.id),
                                      "segment-league": !!segment?.league_id,
                                      "segment-tournament": !!segment?.tournament_id,
                                    })}
                                    onClick={(e: React.MouseEvent) => handleSegmentClick(segment?.id, e)}
                                  >
                                    <div className="segment-container">
                                      <p className="blocked-type-text">{capitalize(segment?.blocked_type)}</p>
                                      {renderSegmentProducts(segment)}
                                      <div className="segment-options-container">
                                        {segment?.league_id && <FontAwesomeIcon size="1x" icon={faUsers} />}
                                        {segment?.tournament_id && <FontAwesomeIcon size="1x" icon={faTrophy} />}
                                        {segment?.credit_card_required && (
                                          <FontAwesomeIcon size="1x" icon={faCreditCard} />
                                        )}
                                        {segment?.web_booking_enabled && (
                                          <FontAwesomeIcon size="1x" icon={faEarthAmericas} />
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                );
                              } else if (segment?.blocked_type === "blocked") {
                                return (
                                  <td
                                    key={locationIndex}
                                    className={classNames("segment-disabled", {
                                      "segment-selected": editSegments.includes(segment?.id),
                                      "segment-league": !!segment?.league_id,
                                      "segment-tournament": !!segment?.tournament_id,
                                    })}
                                    onClick={(e: React.MouseEvent) => handleSegmentClick(segment?.id, e)}
                                  >
                                    <div className="segment-container">
                                      <p className="blocked-type-text">{capitalize(segment?.blocked_type)}</p>
                                      {renderSegmentProducts(segment)}
                                      <div className="segment-options-container">
                                        {segment?.league_id && <FontAwesomeIcon size="1x" icon={faUsers} />}
                                        {segment?.tournament_id && <FontAwesomeIcon size="1x" icon={faTrophy} />}
                                        {segment?.credit_card_required && (
                                          <FontAwesomeIcon size="1x" icon={faCreditCard} />
                                        )}
                                        {segment?.web_booking_enabled && (
                                          <FontAwesomeIcon size="1x" icon={faEarthAmericas} />
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                );
                              } else if (segment?.blocked_type === "booked" && segment?.booking_id) {
                                return (
                                  <td
                                    key={locationIndex}
                                    className={classNames("segment-disabled", {
                                      "segment-selected": editSegments.includes(segment?.id),
                                      "segment-league": !!segment?.league_id,
                                      "segment-tournament": !!segment?.tournament_id,
                                    })}
                                  >
                                    <div className="segment-container">
                                      <p className="blocked-type-text">{capitalize(segment?.blocked_type)}</p>
                                      {renderSegmentProducts(segment)}
                                      <div className="segment-options-container">
                                        {segment?.league_id && <FontAwesomeIcon size="1x" icon={faUsers} />}
                                        {segment?.tournament_id && <FontAwesomeIcon size="1x" icon={faTrophy} />}
                                        {segment?.credit_card_required && (
                                          <FontAwesomeIcon size="1x" icon={faCreditCard} />
                                        )}
                                        {segment?.web_booking_enabled && (
                                          <FontAwesomeIcon size="1x" icon={faEarthAmericas} />
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                );
                              } else {
                                return (
                                  <td
                                    key={locationIndex}
                                    className={classNames("segment-unavailable", {
                                      "segment-selected": editSegments.includes(segment?.id),
                                    })}
                                  ></td>
                                );
                              }
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <Callout
                actions={{
                  primary: { label: t("secure.facility.reservation.edit_segments.045"), onClick: getToSegmentsConfig },
                }}
                type="error"
                title={t("secure.facility.reservation.edit_segments.028")}
                content={t("secure.facility.reservation.edit_segments.029")}
              />
            )}
          </Card.Section>
        </Card>
      </Page>
      <Drawer
        open={settingsOpen}
        title={t("Apply settings")}
        subtitle={settingsSubtitle()}
        onClose={() => setSettingsOpen(false)}
        onOk={handleApplyButton}
        okText={t("secure.facility.reservation.edit_segments.016")}
        okDisabled={editSegments.length === 0}
        backDropClose
        hideBackdrop
        showCancel
        cancelText={t("Cancel")}
        cancelButtonType="text"
        secondaryActions={[{ label: t("secure.facility.reservation.edit_segments.015"), action: clearSettings }]}
      >
        <div key={settingsFormKey} className="edit-segments-drawer">
        <Drawer.Section bordered>
            <Form>
              <FormLayout>
                <FormLayout.Group>
                  <div>
                    <label className="edit-segment-label">{t("secure.facility.reservation.edit_segments.017")}</label>
                    <Select
                      onChange={(value: string) => handleDropDownChange(value, "blocked_type")}
                      defaultValue={paramState?.blocked_type}
                    >
                      {blockedTypes?.map((blocked_type, index) => {
                        return (
                          <Option key={index} value={blocked_type}>
                            {blocked_type !== "none" ? capitalize(blocked_type) : ""}
                          </Option>
                        );
                      })}
                    </Select>
                  </div>
                </FormLayout.Group>
                <FormLayout.Group>
                  {filterState.selectedLeague ? (
                    <div>
                      <div>
                        <label className="edit-segment-label">
                          {t("secure.facility.reservation.edit_segments.018")}{" "}
                          <FontAwesomeIcon size="1x" icon={faUsers} />
                        </label>
                      </div>

                      <div className="selected-container">
                        <div className="event-name">
                          <div>{filterState.selectedLeague.name}</div>

                          <div>
                            <button className="" onClick={removeSelectedLeague}>
                              <Icon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="edit-segment-label">
                        {t("secure.facility.reservation.edit_segments.018")}{" "}
                        <FontAwesomeIcon size="1x" icon={faUsers} />
                      </label>
                      <Select
                        showSearch
                        onSearch={(query: string) =>
                          setFilterState(prevState => ({ ...prevState, leagueSearchValue: query }))
                        }
                        onChange={(value: any, league: ILeague) => handleLeagueChange(value, "league_id", league)}
                        placeholder={t("secure.facility.reservation.edit_segments.043")}
                        allowClear
                        searchValue={filterState.leagueSearchValue}
                        showDropDownOnFocus={true}
                        searching={filterState.leagueSearching}
                      >
                        <Option value={-1} extraValues={{ name: "None", id: -1 }}>
                          <div className="text-semibold text-lg">{t("None")}</div>
                        </Option>
                        {displayLeagues()?.map((league: any, index: number) => {
                          return (
                            <Option value={league?.id} key={index} extraValues={league}>
                              <div className="text-semibold text-lg">{league.name}</div>
                            </Option>
                          );
                        })}
                      </Select>
                    </div>
                  )}
                </FormLayout.Group>
                <FormLayout.Group>
                  {filterState.selectedTournament ? (
                    <div>
                      <div>
                        <label className="edit-segment-label">
                          {t("secure.facility.reservation.edit_segments.019")}{" "}
                          <FontAwesomeIcon size="1x" icon={faTrophy} />
                        </label>
                      </div>

                      <div className="selected-container">
                        <div className="event-name">
                          <div>{filterState.selectedTournament.name}</div>

                          <div>
                            <button className="" onClick={removeSelectedTournament}>
                              <Icon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="edit-segment-label">
                        {t("secure.facility.reservation.edit_segments.019")}{" "}
                        <FontAwesomeIcon size="1x" icon={faTrophy} />
                      </label>
                      <Select
                        showSearch
                        onSearch={(query: string) =>
                          setFilterState(prevState => ({ ...prevState, tournamentSearchValue: query }))
                        }
                        onChange={(value: any, tournament: ITournament) =>
                          handleTournamentChange(value, "tournament_id", tournament)
                        }
                        placeholder={t("secure.facility.reservation.edit_segments.044")}
                        allowClear
                        searchValue={filterState.tournamentSearchValue}
                        showDropDownOnFocus={true}
                        searching={filterState.tournamentSearching}
                      >
                        <Option value={-1} extraValues={{ name: "None", id: -1 }}>
                          <div className="text-semibold text-lg">{t("None")}</div>
                        </Option>

                        {displayTournaments()
                          ?.filter(tournament => !moment(tournament.date).isBefore(moment(), "day"))
                          .map((tournament: ITournament, index: number) => {
                            return (
                              <Option key={index} value={tournament.id} name={tournament.name} extraValues={tournament}>
                                <div className="flex items-center">
                                  <span>{tournament.name}</span>
                                  <span className="shotgun-tournament-date">
                                    {moment(tournament.date).format("LL")}
                                  </span>
                                </div>
                              </Option>
                            );
                          })}

                        <Option disabled value={-2} key={-2} name={""}>
                          <div>
                            <hr style={{ margin: "0px" }} />
                          </div>
                        </Option>

                        {displayTournaments()
                          ?.filter(tournament => moment(tournament.date).isBefore(moment(), "day"))
                          .map((tournament: ITournament, index: number) => {
                            return (
                              <Option key={index} value={tournament.id} name={tournament.name} extraValues={tournament}>
                                <div className="flex items-center">
                                  <span>{tournament.name}</span>
                                  <span className="shotgun-tournament-date">
                                    {moment(tournament.date).format("LL")}
                                  </span>
                                </div>
                              </Option>
                            );
                          })}
                      </Select>
                    </div>
                  )}
                </FormLayout.Group>
              </FormLayout>
            </Form>

            <div className="edit-segments-button-group">
              <Button size="medium" type="default" onClick={openProductModal}>
                {t("secure.facility.reservation.edit_segments.020")}
              </Button>
              {paramState.attachedVariants.map(attached => (
                <div key={attached.variant.id} className="edit-segments-variant-badge-container">
                  <Badge type="primary">
                    <span>
                      {attached.product?.preferred_title
                        ? attached.product.preferred_title
                        : attached.product?.title}{" "}
                      {attached.variant?.title !== attached.product?.title ? ` - ${attached.variant?.title}` : ""}{" "}
                      <LocaleCurrency amount={attached.variant?.price} />
                    </span>
                  </Badge>
                  <SelectNew
                    id={`booking-length-${attached.variant.id}`}
                    className="edit-segments-booking-length"
                    value={attached.bookingMinutes ?? 0}
                    onChange={minutes => setBookingMinutes(attached.variant.id, minutes === 0 ? null : minutes)}
                    options={[
                      { id: 0, label: t("Every booking") },
                      { id: 30, label: t("30 minutes") },
                      { id: 60, label: t("60 minutes") },
                    ]}
                  />
                  <FontAwesomeIcon
                    size="1x"
                    icon={faXmark}
                    onClick={() => detachVariant(attached.variant.id)}
                    className="edit-segments-remove-variant"
                  />
                </div>
              ))}
            </div>

            <div className="edit-segments-setting-choices">
              <YesNoSetting
                id="credit_card_required"
                label={t("Credit card required")}
                value={paramState.credit_card_required}
                onChange={value => setParamState(prevState => ({ ...prevState, credit_card_required: value }))}
              />
              <YesNoSetting
                id="web_booking_enabled"
                label={t("Web booking enabled")}
                value={paramState.web_booking_enabled}
                onChange={value => setParamState(prevState => ({ ...prevState, web_booking_enabled: value }))}
              />
            </div>
        </Drawer.Section>
        </div>
      </Drawer>
      <AddFolderProductsModal
        open={state.productsModalVisible}
        onClose={closeProductModal}
        onAdd={attachProducts}
        search={productPicker.search}
        onSearchChange={productPicker.setSearch}
        browseFilter={productPicker.browseFilter}
        onBrowseFilterChange={productPicker.setBrowseFilter}
        departments={productPicker.departments}
        departmentsLoading={productPicker.departmentsLoading}
        uncategorizedCount={productPicker.uncategorizedCount}
        productsToAdd={productPicker.productsToAdd}
        availableSlotCount={PRODUCT_SELECT_LIMIT}
        totalEmptySlotCount={PRODUCT_SELECT_LIMIT}
        folderProducts={[]}
        productSearchResults={productPicker.productSearchResults}
        productSearchTotalCount={productPicker.productSearchTotalCount}
        productSearchPending={productPicker.productSearchPending}
        productSearchLoadingMore={productPicker.productSearchLoadingMore}
        productSearchSelectAllPending={productPicker.productSearchSelectAllPending}
        onLoadMoreProducts={productPicker.loadMoreProducts}
        onSelectAllMatchingProducts={productPicker.selectAllMatchingProducts}
        onSelectProduct={productPicker.handleSelectProduct}
        onUnselectProduct={productPicker.handleUnselectProduct}
        hideSlots
        showPrice
        enableVariantSelection
        title={t("secure.facility.reservation.edit_segments.032")}
        confirmText={t("secure.facility.reservation.edit_segments.031")}
        browseOnOpen
        onCreateProduct={() => {
          closeProductModal();
          history.push("/admin/product/new");
        }}
        onGoToProducts={() => {
          closeProductModal();
          history.push("/admin/product");
        }}
      />

      <Popup
        open={state.deleteSegmentsPopupVisible}
        type={"warning"}
        title={t("secure.facility.reservation.edit_segments.038")}
        description={t("secure.facility.reservation.edit_segments.039")}
        closable
        cancelText={t("secure.facility.reservation.edit_segments.040")}
        okText={t("secure.facility.reservation.edit_segments.041")}
        onCancel={() => setState(prevState => ({ ...prevState, deleteSegmentsPopupVisible: false }))}
        onOk={deleteSegments}
      />
    </>
  );
}
